import React from 'react'
import { GripVertical, Lock, Edit2, Trash2 } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '../../utils/cn'
import { KanbanStatus, VehicleStatusOption } from '../../types'

interface DraggableStatusItemProps {
  status: KanbanStatus
  index: number
  onEdit: (status: KanbanStatus) => void
  onDelete: (id: string) => void
  vehicleStatusOptions: VehicleStatusOption[]
}

export const DraggableStatusItem: React.FC<DraggableStatusItemProps> = ({
  status,
  index,
  onEdit,
  onDelete,
  vehicleStatusOptions,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id, disabled: status.locked })

  const vehicleStatusLabel = vehicleStatusOptions.find(
    (v) => v.value === status.vehicleStatus
  )?.label

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={cn(
        'flex items-center justify-between p-4 rounded-lg bg-card border border-border transition-all',
        status.locked ? 'bg-surface/40' : 'cursor-move hover:border-accent/40 hover:bg-surface/40'
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {status.locked ? (
          <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <GripVertical className="h-4 w-4" />
          </div>
        )}

        <div className="flex-shrink-0 w-6 h-6 rounded-md bg-muted border border-border flex items-center justify-center text-xs font-semibold text-muted-foreground tabular-nums">
          {index + 1}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: status.color }} />
            <h3 className="font-semibold text-foreground text-sm truncate">{status.label}</h3>
          </div>
          {status.description && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{status.description}</p>
          )}
          {status.vehicleStatus && vehicleStatusLabel && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="text-xs text-muted-foreground">Veículo:</span>
              <span className="text-xs font-medium text-foreground bg-surface border border-border px-1.5 py-0.5 rounded">
                {vehicleStatusLabel}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0 ml-3">
        <button
          onClick={() => onEdit(status)}
          className="p-1.5 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
          title="Editar"
        >
          <Edit2 className="h-3.5 w-3.5" />
        </button>
        {!status.locked && (
          <button
            onClick={() => onDelete(status.id)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            title="Remover"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}
